import { Component, inject, input } from '@angular/core';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import { Observable, switchMap } from 'rxjs';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

type MeteringConceptList = {
  meteringConcepts: ConceptList;
  storageConcepts: ConceptList;
  hasMeteringConceptEntries: boolean;
  hasStorageConceptEntries: boolean;
};

type ConceptList = {
  fullFeedIn: MeteringConceptModel[];
  fullFeedOut: MeteringConceptModel[];
  commercialAccountingForwarding: MeteringConceptModel[];
  other: MeteringConceptModel[]; // Für den Fall, dass es Konzepte gibt, die keinen der drei Haken haben.
};

@Component({
  selector: 'app-grid-registration-metering-concept-v2',
  templateUrl: './grid-registration-metering-concept-v2.component.html',
  styleUrls: ['./grid-registration-metering-concept-v2.component.scss']
})
export class GridRegistrationMeteringConceptV2Component {
  private readonly dataShareService = inject(DataShareService);

  control = input.required<FormControl<MeteringConceptModel['id']>>();
  disable = input<boolean>(false);
  showDisabledMeteringConcepts = input<boolean>(false);

  readonly catalogId$ = this.dataShareService.getSingleTenantConfig$(
    'gridRegistration.meteringConceptCatalogId',
    'default'
  );
  readonly selectedMeteringConcept$: Observable<MeteringConceptModel> = toObservable(this.control).pipe(
    takeUntilDestroyed(),
    switchMap((control) => control.valueChanges.pipe(startWith(control.value))),
    combineLatestWith(this.dataShareService.meteringConcepts.all$),
    map(([selectedId, meteringConcepts]) =>
      selectedId ? meteringConcepts.find((meteringConcept) => meteringConcept.id === selectedId) : null
    )
  );
  readonly meteringConceptList$: Observable<MeteringConceptList> =
    this.dataShareService.meteringConcepts.meteringConcepts$.pipe(
      takeUntilDestroyed(),
      combineLatestWith(
        this.dataShareService.meteringConcepts.storageConcepts$,
        toObservable(this.showDisabledMeteringConcepts)
      ),
      // Filtern nach disabled
      map(([meteringConcepts, storageConcepts, showDisabledMeteringConcepts]) => [
        showDisabledMeteringConcepts
          ? meteringConcepts
          : meteringConcepts.filter((meteringConcept) => !meteringConcept.disabled),
        showDisabledMeteringConcepts
          ? storageConcepts
          : storageConcepts.filter((storageConcept) => !storageConcept.disabled)
      ]),
      // Filtern der Katalog-ID
      combineLatestWith(this.catalogId$),
      map(([[meteringConcepts, storageConcepts], catalogId]) => [
        meteringConcepts.filter((meteringConcept) => meteringConcept.catalogId === catalogId),
        storageConcepts.filter((storageConcept) => storageConcept.catalogId === catalogId)
      ]),
      // Filtern nach Prozesskonfiguration
      combineLatestWith(
        this.dataShareService.getSingleTenantConfig$<(string | number)[]>(
          'gridRegistration.disabledMeteringConcepts',
          []
        ),
        toObservable(this.showDisabledMeteringConcepts)
      ),
      map(([[meteringConcepts, storageConcepts], disabledMeteringConcepts, showDisabledMeteringConcepts]) => [
        showDisabledMeteringConcepts
          ? meteringConcepts
          : meteringConcepts.filter(
              (concept) =>
                !disabledMeteringConcepts.some(
                  (disabledConcept) => concept.id.toString() === disabledConcept.toString()
                )
            ),
        showDisabledMeteringConcepts
          ? storageConcepts
          : storageConcepts.filter(
              (concept) =>
                !disabledMeteringConcepts.some(
                  (disabledConcept) => concept.id.toString() === disabledConcept.toString()
                )
            )
      ]),
      // Sortierung nach Position und dann nach ID (falls Position doppelt vorhanden ist)
      map(([meteringConcepts, storageConcepts]) => [
        meteringConcepts.sort((first, second) => {
          if (Number.isNaN(first.sortPosition) || Number.isNaN(second.sortPosition))
            return first.id - second.id;
          const position = first.sortPosition - second.sortPosition;
          return position !== 0 ? position : first.id - second.id;
        }),
        storageConcepts.sort((first, second) => {
          if (Number.isNaN(first.sortPosition) || Number.isNaN(second.sortPosition))
            return first.id - second.id;
          const position = first.sortPosition - second.sortPosition;
          return position !== 0 ? position : first.id - second.id;
        })
      ]),
      // Mapping zur Anzeige-Liste
      map(([meteringConcepts, storageConcepts]) => {
        const mappedMeteringConcepts: ConceptList = {
          fullFeedIn: meteringConcepts.filter((meteringConcept) => meteringConcept.isFullFeedIn),
          fullFeedOut: meteringConcepts.filter((meteringConcept) => meteringConcept.isFullFeedOut),
          commercialAccountingForwarding: meteringConcepts.filter(
            (meteringConcept) => meteringConcept.isCommercialAccountingForwarding
          ),
          other: meteringConcepts.filter(
            (meteringConcept) =>
              !meteringConcept.isFullFeedIn &&
              !meteringConcept.isFullFeedOut &&
              !meteringConcept.isCommercialAccountingForwarding
          )
        };
        const mappedStorageConcepts: ConceptList = {
          fullFeedIn: storageConcepts.filter((storageConcept) => storageConcept.isFullFeedIn),
          fullFeedOut: storageConcepts.filter((storageConcept) => storageConcept.isFullFeedOut),
          commercialAccountingForwarding: storageConcepts.filter(
            (storageConcept) => storageConcept.isCommercialAccountingForwarding
          ),
          other: storageConcepts.filter(
            (storageConcept) =>
              !storageConcept.isFullFeedIn &&
              !storageConcept.isFullFeedOut &&
              !storageConcept.isCommercialAccountingForwarding
          )
        };

        return {
          meteringConcepts: mappedMeteringConcepts,
          storageConcepts: mappedStorageConcepts,
          hasMeteringConceptEntries: !!meteringConcepts.length, // Ich geh mal davon aus, dass es keine Konzepte gibt, bei denen garkein Haken gesetzt ist, sonst ist die Prüfung hier falsch
          hasStorageConceptEntries: !!storageConcepts.length // Ich geh mal davon aus, dass es keine Konzepte gibt, bei denen garkein Haken gesetzt ist, sonst ist die Prüfung hier falsch
        };
      }),
      shareReplay(1)
    ) as Observable<MeteringConceptList>;
}
